// src/pages/ChatPage.js - ENGLISH VERSION
import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback
} from 'react';
import EmojiPicker from 'emoji-picker-react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Badge,
  IconButton,
  Divider,
  alpha,
  useTheme,
  useMediaQuery,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  Tooltip,
  Skeleton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChatIcon from '@mui/icons-material/Chat';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DoneIcon from '@mui/icons-material/Done';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useLocation } from 'react-router-dom';
import { useSocketContext } from '../context/SocketContext';
import { getStoredFileAccessUrl, openStoredFile } from '../services/fileAccessService';
import { isNativeMobilePlatform } from '../utils/platform';

// ============================================
// HELPER FUNCTIONS
// ============================================

const getSafeName = (conv) => {
  if (!conv || typeof conv !== 'object') return 'User';
  try {
    if (conv.name && typeof conv.name === 'string' && conv.name.trim()) {
      return String(conv.name).trim();
    }
    if (conv.first_name || conv.last_name) {
      const first = (conv.first_name || '').trim();
      const last = (conv.last_name || '').trim();
      const fullName = `${first} ${last}`.trim();
      if (fullName) return fullName;
    }
    if (conv.username && String(conv.username).trim()) {
      return String(conv.username).trim();
    }
    if (conv.email && String(conv.email).includes('@')) {
      const emailName = String(conv.email).split('@')[0];
      if (emailName) return emailName;
    }
    return `User ${conv.id || 'Unknown'}`;
  } catch (error) {
    console.error('❌ Error in getSafeName:', error);
    return 'User';
  }
};

const getInitial = (name) => {
  try {
    if (!name || typeof name !== 'string') return 'U';
    const str = String(name).trim();
    if (!str) return 'U';
    const words = str.split(/\s+/).filter(w => w.length > 0);
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }
    return str[0].toUpperCase();
  } catch (error) {
    console.error('❌ Error in getInitial:', error);
    return 'U';
  }
};

const getAvatarColor = (name) => {
  const colors = ['#1976d2', '#0288d1', '#0097a7', '#00796b', '#5e35b1'];
  try {
    if (!name || typeof name !== 'string') return colors[0];
    const nameStr = String(name).trim();
    if (!nameStr) return colors[0];
    let hash = 0;
    for (let i = 0; i < nameStr.length; i++) {
      hash = ((hash << 5) - hash) + nameStr.charCodeAt(i);
      hash = hash & hash;
    }
    return colors[Math.abs(hash) % colors.length];
  } catch (error) {
    console.error('❌ Error in getAvatarColor:', error);
    return colors[0];
  }
};

const formatConversationTimestamp = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  if (sameDay) {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};

const getConversationPreview = (conv) => {
  if (!conv) return '';
  const previewBase = (() => {
    if (conv.lastMessage && String(conv.lastMessage).trim()) {
      return String(conv.lastMessage).trim();
    }
    if (conv.last_message && String(conv.last_message).trim()) {
      return String(conv.last_message).trim();
    }
    if (conv.email) {
      return String(conv.email);
    }
    return 'No messages yet';
  })();

  if (conv.lastMessageFileName || conv.last_message_file_name || conv.lastMessageFileUrl || conv.last_message_file_url) {
    return `Attachment: ${conv.lastMessageFileName || conv.last_message_file_name || previewBase}`;
  }

  return previewBase;
};

const getConversationPreviewWithAuthor = (conv, currentUserId) => {
  const preview = getConversationPreview(conv);
  const senderId = Number(conv.lastMessageSenderId ?? conv.last_message_sender_id ?? 0);
  if (senderId && currentUserId && senderId === currentUserId) {
    return `You: ${preview}`;
  }
  return preview;
};

const normalizeConversationRecord = (conv) => {
  if (!conv || typeof conv !== 'object' || !conv.id) return null;
  const lastMessageAt = conv.lastMessageAt || conv.last_message_at || conv.timestamp || '';
  return {
    id: conv.id,
    name: conv.name || getSafeName(conv),
    email: conv.email || '',
    avatar: conv.avatar || null,
    username: conv.username || '',
    first_name: conv.first_name || '',
    last_name: conv.last_name || '',
    lastMessage: conv.lastMessage ?? conv.last_message ?? '',
    lastMessageAt,
    lastMessageSenderId: Number(conv.lastMessageSenderId ?? conv.last_message_sender_id ?? 0) || null,
    lastMessageFileUrl: conv.lastMessageFileUrl ?? conv.last_message_file_url ?? '',
    lastMessageFileName: conv.lastMessageFileName ?? conv.last_message_file_name ?? '',
    timestamp: formatConversationTimestamp(lastMessageAt),
    unread: Number(conv.unread ?? conv.unread_count ?? 0),
    online: Boolean(conv.online),
    hasMessages: conv.hasMessages ?? conv.has_messages ?? Boolean(lastMessageAt)
  };
};

const sortConversations = (items) => {
  return [...items].sort((a, b) => {
    const aHas = Boolean(a?.lastMessageAt);
    const bHas = Boolean(b?.lastMessageAt);
    if (aHas !== bHas) return aHas ? -1 : 1;

    const aTime = aHas ? new Date(a.lastMessageAt).getTime() : 0;
    const bTime = bHas ? new Date(b.lastMessageAt).getTime() : 0;
    if (aTime !== bTime) return bTime - aTime;

    return getSafeName(a).localeCompare(getSafeName(b));
  });
};

const MESSAGE_PAGE_SIZE = 30;

const formatMessageDateLabel = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((todayStart.getTime() - targetStart.getTime()) / 86400000);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';

  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
};

const getFileExtension = (nameOrPath) => {
  const clean = String(nameOrPath || '').split('?')[0].toLowerCase();
  const parts = clean.split('.');
  return parts.length > 1 ? parts.pop() : '';
};

const isImageAttachment = (nameOrPath) => {
  return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(getFileExtension(nameOrPath));
};

const getAttachmentLabel = (msg) => {
  return msg.fileName || msg.text || 'Attachment';
};

const RAW_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE_URL = RAW_API_URL.endsWith('/api')
  ? RAW_API_URL
  : `${RAW_API_URL.replace(/\/+$/, '')}/api`;

// ============================================
// COMPONENT MessageInput
// ============================================

const MessageInput = React.memo(({ connected, onSendMessage, onSendFile, onTyping, onStoppedTyping }) => {
  const [inputValue, setInputValue] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  const handleSend = () => {
    if (!inputValue.trim()) return;
    onSendMessage(inputValue);
    setInputValue('');
    if (onStoppedTyping) onStoppedTyping();
  };

  const handleEmojiClick = (emojiObject) => {
    setInputValue(prev => prev + emojiObject.emoji);
    setAnchorEl(null);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file || isUploading) return;

    setUploadError('');
    setIsUploading(true);
    try {
      const uploaded = await onSendFile(file);
      if (!uploaded) setUploadError('The file could not be sent. Try again.');
    } catch (error) {
      setUploadError('The file could not be sent. Try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    if (value.trim() && onTyping) {
      onTyping();

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      typingTimeoutRef.current = setTimeout(() => {
        if (onStoppedTyping) onStoppedTyping();
      }, 1000);
    } else if (!value.trim() && onStoppedTyping) {
      onStoppedTyping();
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        px: { xs: 1, sm: 1.5, md: 2 },
        pt: { xs: 1, md: 2 },
        pb: { xs: 'max(8px, env(safe-area-inset-bottom, 0px))', md: 2 },
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        flexShrink: 0,
        transition: 'all 0.2s ease',
        ...(isFocused && {
          boxShadow: `0 -2px 8px ${alpha('#1976d2', 0.1)}`,
        })
      }}
    >
      <Box sx={{ display: 'flex', gap: { xs: 0.35, sm: 1 }, alignItems: 'flex-end', minWidth: 0 }}>
        <Tooltip title="Attach file" arrow>
          <span>
            <IconButton
              size="small"
              disabled={!connected || isUploading}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                width: 42,
                height: 42,
                flexShrink: 0,
                transition: 'all 0.2s',
                '&:hover': { transform: 'rotate(45deg)' },
                color: 'primary.main',
              }}
            >
              {isUploading ? <CircularProgress size={20} color="inherit" /> : <AttachFileIcon />}
            </IconButton>
          </span>
        </Tooltip>

        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept="image/*,.pdf,.doc,.docx,.txt,.zip"
          onChange={handleFileSelect}
        />

        <Tooltip title="Emoji" arrow>
          <span>
            <IconButton
              size="small"
              disabled={!connected}
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                width: 42,
                height: 42,
                flexShrink: 0,
                transition: 'all 0.2s',
                '&:hover': { color: 'primary.main' }
              }}
            >
              <EmojiEmotionsIcon />
            </IconButton>
          </span>
        </Tooltip>

        <TextField
          fullWidth
          size="small"
          placeholder={connected ? "Type a message..." : "Disconnected..."}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setIsFocused(false);
            if (onStoppedTyping) onStoppedTyping();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          multiline
          maxRows={4}
          disabled={!connected}
          sx={{
            minWidth: 0,
            '& .MuiOutlinedInput-root': {
              minHeight: 42,
              borderRadius: 3,
              bgcolor: alpha('#1976d2', 0.03),
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: alpha('#1976d2', 0.05),
              },
              '&.Mui-focused': {
                bgcolor: 'background.paper',
                boxShadow: `0 0 0 2px ${alpha('#1976d2', 0.1)}`,
              },
            },
          }}
        />

        <Tooltip title="Send message" arrow>
          <span>
            <IconButton
              onClick={handleSend}
              disabled={!inputValue.trim() || !connected}
              sx={{
                width: 42,
                height: 42,
                flexShrink: 0,
                bgcolor: inputValue.trim() && connected ? 'primary.main' : 'transparent',
                color: inputValue.trim() && connected ? 'white' : 'action.disabled',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: inputValue.trim() ? 'scale(1.05)' : 'scale(1)',
                '&:hover': {
                  bgcolor: inputValue.trim() && connected ? 'primary.dark' : 'transparent',
                  transform: inputValue.trim() ? 'scale(1.1)' : 'scale(1)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              <SendIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {uploadError && (
        <Typography variant="caption" color="error" sx={{ display: 'block', px: 0.75, pt: 0.5 }}>
          {uploadError}
        </Typography>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ width: 'min(350px, calc(100vw - 24px))', maxHeight: 'min(400px, 55dvh)' }}>
          <EmojiPicker
            onEmojiClick={handleEmojiClick}
            width="100%"
            height="min(400px, 55dvh)"
          />
        </Box>
      </Menu>
    </Paper>
  );
});

// ============================================
// COMPONENT MessageArea
// ============================================

const SecureChatAttachment = React.memo(({ message }) => {
  const theme = useTheme();
  const [previewUrl, setPreviewUrl] = useState('');
  const [loadError, setLoadError] = useState('');
  const [imageLoadError, setImageLoadError] = useState(false);
  const attachmentLabel = getAttachmentLabel(message);
  const isImageFile = isImageAttachment(message.fileName || message.fileUrl);

  useEffect(() => {
    let active = true;
    setPreviewUrl('');
    setLoadError('');
    setImageLoadError(false);

    getStoredFileAccessUrl(message.fileUrl, 'inline')
      .then((url) => {
        if (active) setPreviewUrl(url);
      })
      .catch((error) => {
        if (active) setLoadError(error.response?.data?.message || error.message || 'Attachment unavailable');
      });

    return () => { active = false; };
  }, [message.fileUrl]);

  const handleOpen = async () => {
    try {
      await openStoredFile(message.fileUrl, 'inline');
    } catch (error) {
      setLoadError(error.response?.data?.message || error.message || 'Attachment unavailable');
    }
  };

  if (isImageFile && !imageLoadError && !loadError) {
    return (
      <Box
        component="button"
        type="button"
        onClick={handleOpen}
        disabled={!previewUrl}
        sx={{
          display: 'block',
          width: '100%',
          p: 0,
          border: 0,
          bgcolor: 'transparent',
          color: 'inherit',
          textAlign: 'left',
          cursor: previewUrl ? 'pointer' : 'wait'
        }}
      >
        {previewUrl ? (
          <Box
            component="img"
            src={previewUrl}
            alt={attachmentLabel}
            onError={() => setImageLoadError(true)}
            sx={{
              width: '100%',
              maxWidth: 320,
              maxHeight: { xs: 240, sm: 280 },
              objectFit: 'cover',
              borderRadius: 2,
              border: '1px solid',
              borderColor: message.sender === 'me' ? 'rgba(255,255,255,0.25)' : 'divider',
              mb: 1
            }}
          />
        ) : (
          <CircularProgress size={22} color="inherit" sx={{ m: 1 }} />
        )}
        <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.9 }}>
          {attachmentLabel}
        </Typography>
      </Box>

    );
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={handleOpen}
      disabled={!previewUrl && !loadError && !imageLoadError}
      sx={{
        width: '100%',
        minWidth: 0,
        border: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 1.1,
        p: 1.15,
        bgcolor: message.sender === 'me'
          ? 'rgba(255,255,255,0.2)'
          : theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(0,0,0,0.05)',
        borderRadius: 1.5,
        color: 'inherit',
        cursor: previewUrl ? 'pointer' : 'wait',
        textAlign: 'left',
        transition: 'all 0.2s',
        '&:hover': {
          bgcolor: message.sender === 'me'
            ? 'rgba(255,255,255,0.3)'
            : theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.15)'
              : 'rgba(0,0,0,0.1)'
        }
      }}
    >
      <Box sx={{ width: 36, height: 36, borderRadius: 1.25, display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,.12)', flexShrink: 0 }}>
        <InsertDriveFileIcon fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 750, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {attachmentLabel}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.82, display: 'block' }}>
          {loadError || imageLoadError
            ? 'Preview unavailable · Open file'
            : previewUrl ? 'Open attachment' : 'Securing attachment...'}
        </Typography>
      </Box>
    </Box>
  );
});

SecureChatAttachment.displayName = 'SecureChatAttachment';

const MessageArea = React.memo(({
  loadingMessages,
  filteredMessages,
  searchInChat,
  onRetryMessage,
  loadingOlderMessages,
  hasMoreMessages,
  showScrollButton,
  handleScroll,
  scrollToBottom,
  messagesContainerRef,
  messagesEndRef,
  theme
}) => {
  const renderedEntries = useMemo(() => {
    const entries = [];
    let previousDateLabel = '';

    filteredMessages.forEach((msg) => {
      const currentDateLabel = formatMessageDateLabel(msg.fullDate);
      if (currentDateLabel && currentDateLabel !== previousDateLabel) {
        entries.push({
          type: 'separator',
          key: `sep-${currentDateLabel}-${msg.id}`,
          label: currentDateLabel
        });
        previousDateLabel = currentDateLabel;
      }

      entries.push({
        type: 'message',
        key: `msg-${msg.id}`,
        message: msg
      });
    });

    return entries;
  }, [filteredMessages]);

  return (
    <Box
      ref={messagesContainerRef}
      onScroll={handleScroll}
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        overflowX: 'hidden',
        p: { xs: 1.25, sm: 2 },
        position: 'relative',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain',
        bgcolor: theme.palette.mode === 'dark'
          ? '#1a2332'
          : alpha(theme.palette.primary.main, 0.02),
        backgroundImage: theme.palette.mode === 'dark'
          ? 'none'
          : `radial-gradient(${alpha(theme.palette.primary.main, 0.05)} 1px, transparent 1px)`,
        backgroundSize: '20px 20px',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: alpha(theme.palette.primary.main, 0.3),
          borderRadius: '4px',
          '&:hover': {
            background: alpha(theme.palette.primary.main, 0.5),
          },
        },
      }}
    >
      {loadingMessages ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[1, 2, 3].map((i) => (
            <Box key={i} sx={{ display: 'flex', justifyContent: i % 2 === 0 ? 'flex-end' : 'flex-start' }}>
              <Skeleton variant="rounded" width={i % 2 === 0 ? '60%' : '45%'} height={60} sx={{ borderRadius: 2 }} />
            </Box>
          ))}
        </Box>
      ) : filteredMessages.length === 0 ? (
        <Box sx={{
          textAlign: 'center',
          my: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}>
          <Box sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}>
            <ChatIcon sx={{ fontSize: 40, opacity: 0.4, color: 'primary.main' }} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {searchInChat.trim() ? 'No messages found' : 'No messages yet'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {searchInChat.trim() ? 'Try different keywords' : 'Send a message to start the conversation'}
          </Typography>
        </Box>
      ) : null}

      {loadingOlderMessages && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <Chip
            size="small"
            label="Loading older messages..."
            variant="outlined"
            icon={<CircularProgress size={14} />}
          />
        </Box>
      )}

      {!hasMoreMessages && filteredMessages.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Start of conversation
          </Typography>
        </Box>
      )}

      {renderedEntries.map((entry) => entry.type === 'separator' ? (
        <Box key={entry.key} sx={{ display: 'flex', justifyContent: 'center', my: { xs: 1, sm: 1.5 } }}>
          <Chip
            size="small"
            label={entry.label}
            sx={{
              bgcolor: alpha(theme.palette.background.paper, 0.75),
              backdropFilter: 'blur(6px)',
              border: '1px solid',
              borderColor: 'divider'
            }}
          />
        </Box>
      ) : (
        (() => {
          const msg = entry.message;
          return (
        <Box
          key={entry.key}
          sx={{
            display: 'flex',
            justifyContent: msg.sender === 'me' ? 'flex-end' : 'flex-start',
            mb: { xs: 1, sm: 1.5 },
            minWidth: 0,
          }}
        >
          <Paper
            elevation={msg.sender === 'me' ? 2 : 0}
            sx={{
              p: { xs: 1.15, sm: 1.5 },
              maxWidth: { xs: '86%', sm: '78%', md: '70%' },
              minWidth: 0,
              bgcolor: msg.sender === 'me'
                ? theme.palette.mode === 'dark' ? '#2196F3' : 'primary.main'
                : theme.palette.mode === 'dark' ? '#2a3647' : 'background.paper',
              color: msg.sender === 'me'
                ? 'white'
                : theme.palette.mode === 'dark' ? '#e3e8ef' : 'text.primary',
              borderRadius: 2.5,
              borderTopLeftRadius: msg.sender === 'me' ? 20 : 6,
              borderTopRightRadius: msg.sender === 'me' ? 6 : 20,
              borderBottomLeftRadius: 20,
              borderBottomRightRadius: 20,
              border: msg.sender === 'them'
                ? theme.palette.mode === 'dark'
                  ? '1px solid rgba(255, 255, 255, 0.08)'
                  : '1px solid'
                : 'none',
              borderColor: msg.sender === 'them' ? 'divider' : 'transparent',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: msg.sender === 'me' ? 3 : 1,
              },
            }}
          >
            {msg.fileUrl ? (
              <SecureChatAttachment message={msg} />
            ) : (
              <Typography variant="body2" sx={{ wordBreak: 'break-word', lineHeight: 1.5, color: 'inherit' }}>
                {msg.text}
              </Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  opacity: msg.sender === 'me' ? 0.85 : 0.7,
                  fontSize: '0.7rem',
                  color: 'inherit',
                }}
              >
                {msg.timestamp}
              </Typography>
              {msg.sender === 'me' && (
                <>
                  {msg.status === 'sending' && <AccessTimeIcon sx={{ fontSize: 14, opacity: 0.7 }} />}
                  {msg.status === 'sent' && <DoneIcon sx={{ fontSize: 14, opacity: 0.85 }} />}
                  {msg.status === 'delivered' && <DoneAllIcon sx={{ fontSize: 14, opacity: 0.85 }} />}
                  {msg.status === 'read' && <DoneAllIcon sx={{ fontSize: 14, opacity: 1, color: '#4fc3f7' }} />}
                  {msg.status === 'error' && (
                    <Tooltip title="Failed to send" arrow>
                      <ErrorOutlineIcon sx={{ fontSize: 14, color: '#ffb4ab' }} />
                    </Tooltip>
                  )}
                </>
              )}
            </Box>

            {msg.sender === 'me' && msg.status === 'error' && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                <Chip
                  size="small"
                  icon={<RefreshIcon sx={{ fontSize: '0.9rem !important' }} />}
                  label="Retry"
                  onClick={() => onRetryMessage?.(msg.id)}
                  sx={{
                    height: 24,
                    bgcolor: msg.sender === 'me' ? 'rgba(255,255,255,0.18)' : alpha(theme.palette.error.main, 0.08),
                    color: 'inherit',
                    '& .MuiChip-icon': {
                      color: 'inherit'
                    }
                  }}
                />
              </Box>
            )}
          </Paper>
        </Box>
          );
        })()
      ))}

      {showScrollButton && (
        <IconButton
          onClick={scrollToBottom}
          sx={{
            position: 'sticky',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: 'primary.main',
            color: 'white',
            boxShadow: 4,
            zIndex: 10,
            '&:hover': {
              bgcolor: 'primary.dark',
              transform: 'translateX(-50%) scale(1.1)',
            },
            transition: 'all 0.2s',
          }}
        >
          <KeyboardArrowDownIcon />
        </IconButton>
      )}

      <div ref={messagesEndRef} />
    </Box>
  );
});

// ============================================
// COMPONENT ConversationList
// ============================================

const ConversationList = React.memo(({
  conversations,
  theme,
  connected,
  loading,
  currentUserId,
  searchQuery,
  setSearchQuery,
  selectedChat,
  setSelectedChat,
  unreadCounts
}) => {
  if (conversations === null) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!Array.isArray(conversations)) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper', p: 4 }}>
        <Typography variant="h6" color="error">Error loading conversations</Typography>
      </Box>
    );
  }

  const hoverBgColor = alpha(theme.palette.primary.main, 0.05);
  const selectedBgColor = alpha(theme.palette.primary.main, 0.12);
  const selectedHoverBgColor = alpha(theme.palette.primary.main, 0.15);
  const searchBgColor = alpha(theme.palette.primary.main, 0.03);
  const searchHoverBgColor = alpha(theme.palette.primary.main, 0.05);
  const primaryMainColor = theme.palette.primary.main;

  const validConversations = conversations.filter(conv => {
    return conv && typeof conv === 'object' && conv.id;
  });

  const filtered = validConversations.filter(conv => {
    if (!searchQuery || !searchQuery.trim()) return true;

    try {
      const query = searchQuery.toLowerCase();
      const name = getSafeName(conv).toLowerCase();
      const email = String(conv.email || '').toLowerCase();
      return name.includes(query) || email.includes(query);
    } catch (error) {
      return false;
    }
  });

  return (
    <Box sx={{
      height: '100%',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.paper'
    }}>
      <Box sx={{ p: { xs: 1.5, md: 2.5 }, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5, minWidth: 0 }}>
          <ChatIcon sx={{ fontSize: 26, color: 'primary.main', flexShrink: 0 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, flex: 1, minWidth: 0, color: 'text.primary' }}>
            Messages
          </Typography>
          <Chip
            label={connected ? 'Connected' : 'Disconnected'}
            size="small"
            color={connected ? 'success' : 'default'}
            sx={{
              fontWeight: 600,
              height: 24,
              '& .MuiChip-label': { px: 1.5 }
            }}
          />
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder="Search conversations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: searchBgColor,
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: searchHoverBgColor,
              },
              '&.Mui-focused': {
                bgcolor: 'background.paper',
              },
            },
          }}
        />
      </Box>

      <Divider />

      {loading ? (
        <Box sx={{ p: 2 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Skeleton variant="circular" width={48} height={48} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="60%" height={20} />
                <Skeleton variant="text" width="80%" height={16} />
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <List sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', p: 0 }}>
          {filtered.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <SearchIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {searchQuery ? 'No users found' : 'No conversations available'}
              </Typography>
            </Box>
          ) : (
            filtered.map((conv) => {
              if (!conv || !conv.id) return null;

              try {
                const displayName = getSafeName(conv);
                if (!displayName) return null;

                const initial = getInitial(displayName);
                const avatarColor = getAvatarColor(displayName);

                return (
                  <ListItem key={conv.id} disablePadding>
                    <ListItemButton
                      selected={selectedChat?.id === conv.id}
                      onClick={() => setSelectedChat(conv)}
                      sx={{
                        py: 1.35,
                        px: { xs: 1.5, md: 2.5 },
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: hoverBgColor,
                        },
                        '&.Mui-selected': {
                          bgcolor: selectedBgColor,
                          borderLeft: `3px solid ${primaryMainColor}`,
                          '&:hover': {
                            bgcolor: selectedHoverBgColor,
                          }
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          variant="dot"
                          sx={{
                            '& .MuiBadge-badge': {
                              bgcolor: conv.online ? '#44b700' : 'transparent',
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              border: `2px solid ${theme.palette.background.paper}`,
                              boxShadow: conv.online ? '0 0 0 2px rgba(68, 183, 0, 0.2)' : 'none',
                            },
                          }}
                        >
                          <Badge
                            badgeContent={unreadCounts[conv.id] || conv.unread || 0}
                            color="error"
                            overlap="circular"
                            sx={{
                              '& .MuiBadge-badge': {
                                fontSize: '0.7rem',
                                height: 20,
                                minWidth: 20,
                                fontWeight: 700,
                              },
                            }}
                          >
                            <Avatar
                              src={conv.avatar}
                              sx={{
                                bgcolor: avatarColor,
                                width: 48,
                                height: 48,
                                fontWeight: 600,
                                fontSize: '1.1rem'
                              }}
                            >
                              {initial}
                            </Avatar>
                          </Badge>
                        </Badge>
                      </ListItemAvatar>

                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 600, mb: 0.5, flex: 1, minWidth: 0 }}
                              noWrap
                            >
                              {displayName}
                            </Typography>
                            {conv.timestamp && (
                              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                {conv.timestamp}
                              </Typography>
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="body2"
                              color={(unreadCounts[conv.id] || conv.unread || 0) > 0 ? 'text.primary' : 'text.secondary'}
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: '0.85rem',
                                fontWeight: (unreadCounts[conv.id] || conv.unread || 0) > 0 ? 600 : 400,
                                flex: 1,
                                minWidth: 0
                              }}
                            >
                              {getConversationPreviewWithAuthor(conv, currentUserId)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                );
              } catch (error) {
                return null;
              }
            })
          )}
        </List>
      )}
    </Box>
  );
});

// ============================================
// COMPONENT ChatView
// ============================================

const ChatView = React.memo(({
  selectedChat,
  isMobile,
  setSelectedChat,
  theme,
  typingUsers,
  isSearchOpen,
  searchInChat,
  toggleSearch,
  setSearchInChat,
  loadingMessages,
  filteredMessages,
  onRetryMessage,
  loadingOlderMessages,
  hasMoreMessages,
  showScrollButton,
  handleScroll,
  scrollToBottom,
  messagesContainerRef,
  messagesEndRef,
  connected,
  onRefreshConversation,
  onMarkConversationRead,
  menuActionFeedback,
  setMenuActionFeedback,
  onSendMessage,
  onSendFile,
  onTyping,
  onStoppedTyping
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  if (!selectedChat) {
    return (
      <Box sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        color: 'text.secondary',
        bgcolor: alpha(theme.palette.primary.main, 0.02),
      }}>
        <Box sx={{
          width: 120,
          height: 120,
          borderRadius: '50%',
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
        }}>
          <ChatIcon sx={{ fontSize: 60, color: 'primary.main', opacity: 0.6 }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Select a conversation
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Choose a contact from the list to start chatting
        </Typography>
        <Chip
          icon={connected ? <DoneAllIcon /> : <AccessTimeIcon />}
          label={connected ? 'Connected' : 'Disconnected'}
          color={connected ? 'success' : 'default'}
          size="small"
        />
      </Box>
    );
  }

  const chatName = getSafeName(selectedChat);
  const chatInitial = getInitial(chatName);
  const menuOpen = Boolean(menuAnchorEl);

  const handleCopyEmail = async () => {
    if (!selectedChat?.email || !navigator?.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(selectedChat.email);
      setMenuActionFeedback?.('Email copied');
    } catch (error) {
      setMenuActionFeedback?.('Could not copy email');
    } finally {
      setMenuAnchorEl(null);
    }
  };

  return (
    <Box sx={{
      height: '100%',
      minHeight: 0,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <Paper elevation={0} sx={{
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        zIndex: 1,
        flexShrink: 0
      }}>
        <Box sx={{ p: { xs: 1, sm: 1.5, md: 2 }, display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1.5 }, minWidth: 0 }}>
          {isMobile && (
            <IconButton
              onClick={() => setSelectedChat(null)}
              aria-label="Back to conversations"
              sx={{ width: 40, height: 40, flexShrink: 0, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) } }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}

          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            variant="dot"
            sx={{
              '& .MuiBadge-badge': {
                bgcolor: selectedChat.online ? '#44b700' : '#bdbdbd',
                width: 10,
                height: 10,
                borderRadius: '50%',
                border: `2px solid ${theme.palette.background.paper}`,
                boxShadow: selectedChat.online ? '0 0 0 2px rgba(68, 183, 0, 0.2)' : 'none',
              },
            }}
          >
            <Avatar
              src={selectedChat.avatar}
              sx={{
                bgcolor: getAvatarColor(chatName),
                width: { xs: 38, sm: 44 },
                height: { xs: 38, sm: 44 },
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {chatInitial}
            </Avatar>
          </Badge>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, lineHeight: 1.25 }}>
              {chatName}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: Object.keys(typingUsers).length > 0
                  ? 'primary.main'
                  : selectedChat.online ? 'success.main' : 'text.secondary',
                fontWeight: 500,
              }}
            >
              {Object.keys(typingUsers).length > 0
                ? 'typing...'
                : selectedChat.online ? 'Online' : 'Offline'
              }
            </Typography>
          </Box>

          <Tooltip title="Search in chat" arrow>
            <IconButton aria-label="Search messages" onClick={toggleSearch} color={isSearchOpen ? 'primary' : 'default'} sx={{ width: 40, height: 40, flexShrink: 0 }}>
              <SearchIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="More options" arrow>
            <IconButton aria-label="Conversation options" onClick={(e) => setMenuAnchorEl(e.currentTarget)} sx={{ width: 40, height: 40, flexShrink: 0 }}>
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Menu
          anchorEl={menuAnchorEl}
          open={menuOpen}
          onClose={() => setMenuAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              onRefreshConversation?.();
              setMenuActionFeedback?.('Conversation refreshed');
              setMenuAnchorEl(null);
            }}
          >
            Refresh conversation
          </MenuItem>
          <MenuItem
            onClick={() => {
              onMarkConversationRead?.();
              setMenuActionFeedback?.('Conversation marked as read');
              setMenuAnchorEl(null);
            }}
          >
            Mark as read
          </MenuItem>
          <MenuItem
            onClick={() => {
              scrollToBottom();
              setMenuActionFeedback?.('Jumped to latest message');
              setMenuAnchorEl(null);
            }}
          >
            Go to latest
          </MenuItem>
          <MenuItem
            onClick={() => {
              setSearchInChat('');
              setMenuActionFeedback?.('Search cleared');
              setMenuAnchorEl(null);
            }}
            disabled={!searchInChat}
          >
            Clear search
          </MenuItem>
          <MenuItem onClick={handleCopyEmail} disabled={!selectedChat?.email}>
            Copy email
          </MenuItem>
        </Menu>

        {menuActionFeedback && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {menuActionFeedback}
            </Typography>
          </Box>
        )}

        {isSearchOpen && (
          <Box sx={{ px: 2, pb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search messages..."
              value={searchInChat}
              onChange={(e) => setSearchInChat(e.target.value)}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchInChat.trim() && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchInChat('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Box>
        )}
      </Paper>

      <MessageArea
        loadingMessages={loadingMessages}
        filteredMessages={filteredMessages}
        searchInChat={searchInChat}
        onRetryMessage={onRetryMessage}
        loadingOlderMessages={loadingOlderMessages}
        hasMoreMessages={hasMoreMessages}
        showScrollButton={showScrollButton}
        handleScroll={handleScroll}
        scrollToBottom={scrollToBottom}
        messagesContainerRef={messagesContainerRef}
        messagesEndRef={messagesEndRef}
        theme={theme}
      />

      <MessageInput
        connected={connected}
        onSendMessage={onSendMessage}
        onSendFile={onSendFile}
        onTyping={onTyping}
        onStoppedTyping={onStoppedTyping}
      />
    </Box>
  );
});

// ============================================
// MAIN COMPONENT ChatPage
// ============================================

export default function ChatPage() {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const nativeMobile = isNativeMobilePlatform();
  const { socket, connected } = useSocketContext();

  // States
  const [selectedChat, setSelectedChat] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const [unreadCounts, setUnreadCounts] = useState({});
  const [searchInChat, setSearchInChat] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [messagesOffset, setMessagesOffset] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [menuActionFeedback, setMenuActionFeedback] = useState('');

  // Refs
  const messagesEndRef = useRef(null);
  const selectedChatRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingTimeouts = useRef({});
  const socketRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const prependScrollRestoreRef = useRef(null);
  const handledDeepLinkRef = useRef('');

  // ============================================
  // EFFECTS - Load current user
  // ============================================

  useEffect(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        setCurrentUser(userData.name || 'User');
        setCurrentUserId(userData.id);
      } else {
        setCurrentUser('User');
      }
    } catch (error) {
      console.error('❌ Error loading user:', error);
      setCurrentUser('User');
    }
  }, []);

  // ============================================
  // EFFECTS - Load conversations
  // ============================================

  useEffect(() => {
    if (currentUserId) {
      loadConversations();
    }
  }, [currentUserId]);

  useEffect(() => {
    if (!Array.isArray(conversations) || !conversations.length) return;
    const requestedUserId = Number(new URLSearchParams(location.search).get('user'));
    if (!Number.isInteger(requestedUserId) || requestedUserId <= 0) return;
    const deepLinkKey = `${location.search}:${requestedUserId}`;
    if (handledDeepLinkRef.current === deepLinkKey) return;
    const conversation = conversations.find((item) => Number(item.id) === requestedUserId);
    if (!conversation) return;
    handledDeepLinkRef.current = deepLinkKey;
    setSelectedChat(conversation);
  }, [conversations, location.search]);

  const loadConversations = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      setConversations([]);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_BASE_URL}/messages/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && Array.isArray(data.conversations)) {
        const normalized = data.conversations
          .map((item) => normalizeConversationRecord(item))
          .filter(Boolean);

        const unreadMap = {};
        normalized.forEach((item) => {
          unreadMap[item.id] = item.unread || 0;
        });

        setUnreadCounts(unreadMap);
        setConversations(sortConversations(normalized));
      } else {
        setConversations([]);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error('❌ Error loading conversations:', error);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  const updateConversationState = useCallback((userId, updater) => {
    setConversations((prev) => {
      if (!Array.isArray(prev)) return prev;
      const found = prev.some((conv) => conv.id === userId);
      const next = found
        ? prev.map((conv) => {
          if (conv.id !== userId) return conv;
          const updated = updater(conv);
          return normalizeConversationRecord(updated) || conv;
        })
        : prev;
      return sortConversations(next);
    });
  }, []);

  const applyUnreadCounts = useCallback((counts = {}) => {
    setUnreadCounts(counts);
    setConversations((prev) => {
      if (!Array.isArray(prev)) return prev;
      const next = prev.map((conv) => ({
        ...conv,
        unread: Number(counts[conv.id] || 0)
      }));
      return sortConversations(next);
    });
  }, []);

  const markMessagesAsRead = useCallback(async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/messages/mark-read/${userId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      setUnreadCounts(prev => ({ ...prev, [userId]: 0 }));
      updateConversationState(userId, (conv) => ({ ...conv, unread: 0 }));
      if (data?.success && Array.isArray(data.readMessageIds) && data.readMessageIds.length) {
        const readSet = new Set(data.readMessageIds);
        setMessages((prev) => prev.map((msg) => (
          readSet.has(msg.id) ? { ...msg, status: 'read' } : msg
        )));
      }
    } catch (error) {
      console.error('❌ Error marking as read:', error);
    }
  }, [updateConversationState]);

  // ============================================
  // FUNCTION loadMessages
  // ============================================

  const loadMessages = useCallback(async (userId, options = {}) => {
    const { appendOlder = false } = options;
    try {
      if (appendOlder) {
        setLoadingOlderMessages(true);
      } else {
        setLoadingMessages(true);
      }
      const token = localStorage.getItem('token');

      if (!currentUserId) {
        console.error('❌ User without ID');
        return;
      }

      const requestOffset = appendOlder ? messagesOffset : 0;
      if (appendOlder && messagesContainerRef.current) {
        prependScrollRestoreRef.current = {
          previousHeight: messagesContainerRef.current.scrollHeight,
          previousTop: messagesContainerRef.current.scrollTop
        };
      }

      const response = await fetch(`${API_BASE_URL}/messages/${currentUserId}/${userId}?limit=${MESSAGE_PAGE_SIZE}&offset=${requestOffset}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.success) {
        const formattedMessages = data.messages.map(msg => ({
          id: msg.id,
          text: msg.message,
          sender: msg.sender_id === currentUserId ? 'me' : 'them',
          fileUrl: msg.file_url || null,
          fileName: msg.file_name || null,
          status: msg.is_read ? 'read' : 'delivered',
          // Changed locale to en-US
          timestamp: new Date(msg.created_at).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
          }),
          fullDate: new Date(msg.created_at),
        }));

        setHasMoreMessages(formattedMessages.length === MESSAGE_PAGE_SIZE);
        setMessagesOffset(requestOffset + formattedMessages.length);

        if (appendOlder) {
          setMessages((prev) => [...formattedMessages, ...prev]);
        } else {
          isNearBottomRef.current = true;
          setMessages(formattedMessages);
        }
      }
    } catch (error) {
      console.error('❌ Error loading messages:', error);
      if (!appendOlder) {
        setMessages([]);
      }
    } finally {
      if (appendOlder) {
        setLoadingOlderMessages(false);
      } else {
        setLoadingMessages(false);
      }
    }
  }, [currentUserId, messagesOffset]);

  // ============================================
  // SMART SCROLL useLayoutEffect
  // ============================================

  useLayoutEffect(() => {
    if (loadingMessages || !messagesContainerRef.current) {
      return;
    }

    if (prependScrollRestoreRef.current) {
      const { previousHeight, previousTop } = prependScrollRestoreRef.current;
      const newHeight = messagesContainerRef.current.scrollHeight;
      messagesContainerRef.current.scrollTop = newHeight - previousHeight + previousTop;
      prependScrollRestoreRef.current = null;
      return;
    }

    const lastMessage = messages[messages.length - 1];
    const isMyMessage = lastMessage?.sender === 'me';
    const wasNearBottom = isNearBottomRef.current;

    if (isMyMessage || wasNearBottom) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      isNearBottomRef.current = true;
    }

  }, [messages, loadingMessages]);

  // ============================================
  // EFFECTS - Socket.IO Configuration
  // ============================================

  useEffect(() => {
    socketRef.current = socket || null;
  }, [socket]);

  useEffect(() => {
    if (!socket || !currentUserId) return;

    const handleConnect = () => {
      socket.emit('join_room', currentUserId);
    };
    if (socket.connected) handleConnect();
    socket.on('connect', handleConnect);

    const handleUserList = (userIds = []) => {
      setOnlineUsers(userIds);
      setConversations((prev) => {
        if (!Array.isArray(prev)) return prev;
        const next = prev.map((conv) => ({
          ...conv,
          online: userIds.includes(conv.id)
        }));
        return sortConversations(next);
      });
    };

    const handleReceiveMessage = (data) => {
      setMessages((prev) => {
        const currentSelectedChat = selectedChatRef.current;

        if (!currentSelectedChat) {
          if (data.toUserId === currentUserId) {
            setUnreadCounts((prevCounts) => {
              const nextCounts = {
                ...prevCounts,
                [data.fromUserId]: (prevCounts[data.fromUserId] || 0) + 1
              };
              updateConversationState(data.fromUserId, (conv) => ({
                ...conv,
                lastMessage: data.message || conv.lastMessage,
                lastMessageAt: data.timestamp || new Date().toISOString(),
                lastMessageSenderId: data.fromUserId,
                lastMessageFileUrl: data.fileUrl || '',
                lastMessageFileName: data.fileName || '',
                unread: nextCounts[data.fromUserId] || 0,
                hasMessages: true
              }));
              return nextCounts;
            });
          }
          return prev;
        }

        const isRelevantMessage =
          (data.fromUserId === currentSelectedChat.id && data.toUserId === currentUserId) ||
          (data.fromUserId === currentUserId && data.toUserId === currentSelectedChat.id);

        if (!isRelevantMessage) {
          if (data.toUserId === currentUserId) {
            setUnreadCounts((prevCounts) => {
              const nextCounts = {
                ...prevCounts,
                [data.fromUserId]: (prevCounts[data.fromUserId] || 0) + 1
              };
              updateConversationState(data.fromUserId, (conv) => ({
                ...conv,
                lastMessage: data.message || conv.lastMessage,
                lastMessageAt: data.timestamp || new Date().toISOString(),
                lastMessageSenderId: data.fromUserId,
                lastMessageFileUrl: data.fileUrl || '',
                lastMessageFileName: data.fileName || '',
                unread: nextCounts[data.fromUserId] || 0,
                hasMessages: true
              }));
              return nextCounts;
            });
          }
          return prev;
        }

        const isDuplicate = prev.some((msg) => {
          if (msg.id === data.messageId) return true;
          const isSameText = msg.text === data.message;
          const isSameFile = data.fileUrl && msg.fileUrl === data.fileUrl;
          const isRecent = msg.fullDate && (Date.now() - msg.fullDate.getTime()) < 2000;
          return (isSameText || isSameFile) && isRecent && msg.sender !== 'me';
        });
        if (isDuplicate) return prev;

        const newMessage = {
          id: data.messageId || `ws-${data.fromUserId}-${Date.now()}`,
          text: data.message,
          sender: data.fromUserId === currentUserId ? 'me' : 'them',
          fileUrl: data.fileUrl || null,
          fileName: data.fileName || null,
          status: 'delivered',
          timestamp: new Date().toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
          }),
          fullDate: new Date(),
        };

        if (data.toUserId === currentUserId && data.messageId) {
          socket.emit('message_delivered', {
            messageId: data.messageId,
            toUserId: data.fromUserId
          });

          if (currentSelectedChat && data.fromUserId === currentSelectedChat.id) {
            socket.emit('message_read', {
              messageId: data.messageId,
              toUserId: data.fromUserId
            });
            setUnreadCounts((prevCounts) => ({
              ...prevCounts,
              [data.fromUserId]: 0
            }));
          }
        }

        const counterpartUserId = data.fromUserId === currentUserId ? data.toUserId : data.fromUserId;
        updateConversationState(counterpartUserId, (conv) => ({
          ...conv,
          lastMessage: data.message || conv.lastMessage,
          lastMessageAt: data.timestamp || new Date().toISOString(),
          lastMessageSenderId: data.fromUserId,
          lastMessageFileUrl: data.fileUrl || '',
          lastMessageFileName: data.fileName || '',
          unread: 0,
          hasMessages: true
        }));

        return [...prev, newMessage];
      });
    };

    const handleTyping = (data) => {
      if (selectedChatRef.current && data.fromUserId === selectedChatRef.current.id) {
        setTypingUsers((prev) => ({
          ...prev,
          [data.fromUserId]: data.fromUsername
        }));

        if (typingTimeouts.current[data.fromUserId]) {
          clearTimeout(typingTimeouts.current[data.fromUserId]);
        }

        typingTimeouts.current[data.fromUserId] = setTimeout(() => {
          setTypingUsers((prev) => {
            const updated = { ...prev };
            delete updated[data.fromUserId];
            return updated;
          });
        }, 3000);
      }
    };

    const handleStoppedTyping = (data) => {
      setTypingUsers((prev) => {
        const updated = { ...prev };
        delete updated[data.fromUserId];
        return updated;
      });

      if (typingTimeouts.current[data.fromUserId]) {
        clearTimeout(typingTimeouts.current[data.fromUserId]);
        delete typingTimeouts.current[data.fromUserId];
      }
    };

    const handleDelivered = (data) => {
      setMessages((prev) => prev.map((msg) => (
        msg.id === data.messageId ? { ...msg, status: 'delivered' } : msg
      )));
    };

    const handleRead = (data) => {
      setMessages((prev) => prev.map((msg) => (
        msg.id === data.messageId ? { ...msg, status: 'read' } : msg
      )));
    };

    const handleBatchRead = (data) => {
      if (!Array.isArray(data?.messageIds) || !data.messageIds.length) return;
      const readSet = new Set(data.messageIds);
      setMessages((prev) => prev.map((msg) => (
        readSet.has(msg.id) ? { ...msg, status: 'read' } : msg
      )));
    };

    const handleUnreadCounts = (payload) => {
      if (!payload || typeof payload !== 'object') return;
      if (payload.userId && payload.userId !== currentUserId) return;
      applyUnreadCounts(payload.unreadCounts || {});
    };

    socket.on('user_list', handleUserList);
    socket.on('receive_message', handleReceiveMessage);
    socket.on('user_typing', handleTyping);
    socket.on('user_stopped_typing', handleStoppedTyping);
    socket.on('message_delivered_confirmation', handleDelivered);
    socket.on('message_read_confirmation', handleRead);
    socket.on('messages_read', handleBatchRead);
    socket.on('messages_unread_counts', handleUnreadCounts);

    return () => {
      Object.values(typingTimeouts.current).forEach((timeout) => {
        if (timeout) clearTimeout(timeout);
      });
      typingTimeouts.current = {};

      socket.off('connect', handleConnect);
      socket.off('user_list', handleUserList);
      socket.off('receive_message', handleReceiveMessage);
      socket.off('user_typing', handleTyping);
      socket.off('user_stopped_typing', handleStoppedTyping);
      socket.off('message_delivered_confirmation', handleDelivered);
      socket.off('message_read_confirmation', handleRead);
      socket.off('messages_read', handleBatchRead);
      socket.off('messages_unread_counts', handleUnreadCounts);
    };
  }, [socket, currentUserId, applyUnreadCounts, updateConversationState]);

  // ============================================
  // EFFECTS - Update selectedChatRef
  // ============================================

  useEffect(() => {
    selectedChatRef.current = selectedChat;
  }, [selectedChat]);

  useEffect(() => {
    if (!selectedChat?.id || !Array.isArray(conversations)) return;
    const updated = conversations.find((conv) => conv.id === selectedChat.id);
    if (updated && updated !== selectedChat) {
      setSelectedChat(updated);
    }
  }, [conversations, selectedChat]);

  useEffect(() => {
    setMenuActionFeedback('');
    setSearchInChat('');
    setIsSearchOpen(false);
    setMessagesOffset(0);
    setHasMoreMessages(true);
    prependScrollRestoreRef.current = null;
  }, [selectedChat?.id]);

  // ============================================
  // EFFECTS - Load Messages
  // ============================================

  const filteredMessages = useMemo(() => {
    if (!searchInChat.trim()) return messages;
    return messages.filter(msg =>
      String(msg.text || msg.fileName || '').toLowerCase().includes(searchInChat.toLowerCase())
    );
  }, [searchInChat, messages]);

  useEffect(() => {
    if (selectedChat?.id) {
      loadMessages(selectedChat.id);
      markMessagesAsRead(selectedChat.id);
    }
  }, [selectedChat?.id, currentUserId, loadMessages, markMessagesAsRead]);

  // ============================================
  // HANDLERS (WITH useCallback)
  // ============================================

  const handleScroll = useCallback((e) => {
    const container = e.target;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (
      scrollTop < 120
      && hasMoreMessages
      && !loadingOlderMessages
      && !loadingMessages
      && selectedChat?.id
    ) {
      loadMessages(selectedChat.id, { appendOlder: true });
    }

    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    const isNearBottom = distanceFromBottom < 100;
    isNearBottomRef.current = isNearBottom;

    const shouldShow = !isNearBottom && messages.length > 0;

    setShowScrollButton(prev => {
      if (prev === shouldShow) return prev;
      return shouldShow;
    });
  }, [messages.length, hasMoreMessages, loadingOlderMessages, loadingMessages, selectedChat, loadMessages]);

  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
      setShowScrollButton(false);
      isNearBottomRef.current = true;
    }
  }, []);

  const toggleSearch = useCallback(() => {
    setIsSearchOpen((prev) => {
      if (prev) {
        setSearchInChat('');
      }
      return !prev;
    });
  }, []);

  const handleRefreshConversation = useCallback(() => {
    if (!selectedChat?.id) return;
    setMessagesOffset(0);
    setHasMoreMessages(true);
    prependScrollRestoreRef.current = null;
    loadMessages(selectedChat.id, { appendOlder: false });
  }, [selectedChat, loadMessages]);

  const handleMarkConversationRead = useCallback(() => {
    if (!selectedChat?.id) return;
    markMessagesAsRead(selectedChat.id);
  }, [selectedChat, markMessagesAsRead]);

  const persistMessage = useCallback(async ({ receiverId, messageText }) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/messages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        sender_id: currentUserId,
        receiver_id: receiverId,
        message: messageText
      })
    });

    return response.json();
  }, [currentUserId]);

  const handleSendMessage = useCallback(async (messageText) => {
    if (!connected || !selectedChat || !currentUserId) return;

    const tempId = Date.now();
    const newMessage = {
      id: tempId,
      text: messageText,
      sender: 'me',
      status: 'sending',
      // Changed locale to en-US
      timestamp: new Date().toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      }),
      fullDate: new Date(),
    };

    setMessages(prev => [...prev, newMessage]);
    isNearBottomRef.current = true;

    try {
      const data = await persistMessage({
        receiverId: selectedChat.id,
        messageText
      });

      if (data.success) {
        setMessages(prev =>
          prev.map(msg =>
            msg.id === tempId
              ? { ...msg, id: data.messageId, status: 'sent' }
              : msg
          )
        );

        updateConversationState(selectedChat.id, (conv) => ({
          ...conv,
          lastMessage: messageText,
          lastMessageAt: new Date().toISOString(),
          lastMessageSenderId: currentUserId,
          lastMessageFileUrl: '',
          lastMessageFileName: '',
          unread: 0,
          hasMessages: true
        }));

        if (socketRef.current) {
          socketRef.current.emit('send_message', {
            toUserId: selectedChat.id,
            fromUserId: currentUserId,
            fromUsername: currentUser,
            message: messageText,
            messageId: data.messageId
          });
        }
      } else {
        throw new Error(data?.message || 'Message could not be saved');
      }
    } catch (error) {
      console.error('❌ Error saving message:', error);
      setMessages(prev =>
        prev.map(msg =>
          msg.id === tempId ? { ...msg, status: 'error' } : msg
        )
      );
    }
  }, [connected, selectedChat, currentUserId, currentUser, persistMessage, updateConversationState]);

  const handleRetryMessage = useCallback(async (messageId) => {
    const failedMessage = messages.find((msg) => msg.id === messageId);
    if (!failedMessage || failedMessage.sender !== 'me' || failedMessage.status !== 'error') return;
    if (!connected || !selectedChat || !currentUserId) return;

    setMessages((prev) => prev.map((msg) => (
      msg.id === messageId ? { ...msg, status: 'sending' } : msg
    )));
    isNearBottomRef.current = true;

    try {
      const data = await persistMessage({
        receiverId: selectedChat.id,
        messageText: failedMessage.text
      });

      if (data.success) {
        setMessages((prev) => prev.map((msg) => (
          msg.id === messageId
            ? {
              ...msg,
              id: data.messageId,
              status: 'sent',
              fullDate: new Date(),
              timestamp: new Date().toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit'
              })
            }
            : msg
        )));

        updateConversationState(selectedChat.id, (conv) => ({
          ...conv,
          lastMessage: failedMessage.text,
          lastMessageAt: new Date().toISOString(),
          lastMessageSenderId: currentUserId,
          lastMessageFileUrl: '',
          lastMessageFileName: '',
          unread: 0,
          hasMessages: true
        }));

        if (socketRef.current) {
          socketRef.current.emit('send_message', {
            toUserId: selectedChat.id,
            fromUserId: currentUserId,
            fromUsername: currentUser,
            message: failedMessage.text,
            messageId: data.messageId
          });
        }
      } else {
        throw new Error(data?.message || 'Message could not be retried');
      }
    } catch (error) {
      console.error('❌ Error retrying message:', error);
      setMessages((prev) => prev.map((msg) => (
        msg.id === messageId ? { ...msg, status: 'error' } : msg
      )));
    }
  }, [messages, connected, selectedChat, currentUserId, currentUser, persistMessage, updateConversationState]);

  const handleSendFile = useCallback(async (file) => {
    if (!connected || !selectedChat || !currentUserId) return false;

    isNearBottomRef.current = true;

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('sender_id', currentUserId);
      formData.append('receiver_id', selectedChat.id);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/messages/file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        const newMessage = {
          id: data.messageId,
          text: file.name,
          fileUrl: data.fileUrl,
          fileName: file.name,
          sender: 'me',
          status: 'sent',
          // Changed locale to en-US
          timestamp: new Date().toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
          }),
          fullDate: new Date(),
        };

        setMessages(prev => [...prev, newMessage]);
        updateConversationState(selectedChat.id, (conv) => ({
          ...conv,
          lastMessage: file.name,
          lastMessageAt: new Date().toISOString(),
          lastMessageSenderId: currentUserId,
          lastMessageFileUrl: data.fileUrl,
          lastMessageFileName: file.name,
          unread: 0,
          hasMessages: true
        }));

        if (socketRef.current) {
          socketRef.current.emit('send_message', {
            toUserId: selectedChat.id,
            fromUserId: currentUserId,
            fromUsername: currentUser,
            message: file.name,
            fileUrl: data.fileUrl,
            fileName: file.name,
            messageId: data.messageId
          });
        }
        return true;
      }
      throw new Error(data?.message || 'File could not be sent');
    } catch (error) {
      console.error('❌ Error sending file:', error);
      return false;
    }
  }, [connected, selectedChat, currentUserId, currentUser, updateConversationState]);

  const handleTyping = useCallback(() => {
    if (socketRef.current && selectedChat && connected) {
      socketRef.current.emit('user_typing', {
        fromUserId: currentUserId,
        toUserId: selectedChat.id,
        fromUsername: currentUser
      });
    }
  }, [socketRef, selectedChat, connected, currentUserId, currentUser]);

  const handleStoppedTyping = useCallback(() => {
    if (socketRef.current && selectedChat && connected) {
      socketRef.current.emit('user_stopped_typing', {
        fromUserId: currentUserId,
        toUserId: selectedChat.id
      });
    }
  }, [socketRef, selectedChat, connected, currentUserId]);

  // ============================================
  // COMPONENT RENDER
  // ============================================

  return (
    <Box sx={{
      p: { xs: 0, md: 2 },
      width: '100%',
      height: nativeMobile ? '100%' : 'calc(100dvh - 64px)',
      minHeight: 0,
      overflow: 'hidden'
    }}>
      <Paper elevation={3} sx={{ height: '100%', minHeight: 0, minWidth: 0, borderRadius: { xs: 0, md: 3 }, overflow: 'hidden', display: 'flex', bgcolor: 'background.paper' }}>
        {isMobile ? (
          !selectedChat ? (
            <Box sx={{ width: '100%', height: '100%', minHeight: 0, overflow: 'hidden' }}>
              <ConversationList
                conversations={conversations}
                theme={theme}
                connected={connected}
                loading={loading}
                currentUserId={currentUserId}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                selectedChat={selectedChat}
                setSelectedChat={setSelectedChat}
                unreadCounts={unreadCounts}
              />
            </Box>
          ) : (
            <Box sx={{ width: '100%', height: '100%', minHeight: 0, overflow: 'hidden' }}>
              <ChatView
                selectedChat={selectedChat}
                isMobile={isMobile}
                setSelectedChat={setSelectedChat}
                theme={theme}
                typingUsers={typingUsers}
                isSearchOpen={isSearchOpen}
                searchInChat={searchInChat}
                toggleSearch={toggleSearch}
                setSearchInChat={setSearchInChat}
                loadingMessages={loadingMessages}
                filteredMessages={filteredMessages}
                onRetryMessage={handleRetryMessage}
                loadingOlderMessages={loadingOlderMessages}
                hasMoreMessages={hasMoreMessages}
                showScrollButton={showScrollButton}
                handleScroll={handleScroll}
                scrollToBottom={scrollToBottom}
                messagesContainerRef={messagesContainerRef}
                messagesEndRef={messagesEndRef}
                connected={connected}
                onRefreshConversation={handleRefreshConversation}
                onMarkConversationRead={handleMarkConversationRead}
                menuActionFeedback={menuActionFeedback}
                setMenuActionFeedback={setMenuActionFeedback}
                onSendMessage={handleSendMessage}
                onSendFile={handleSendFile}
                onTyping={handleTyping}
                onStoppedTyping={handleStoppedTyping}
              />
            </Box>
          )
        ) : (
          <>
            <Box sx={{
              width: 360,
              height: '100%',
              minHeight: 0,
              borderRight: '1px solid',
              borderColor: 'divider',
              boxShadow: `2px 0 8px ${alpha('#000', 0.03)}`,
            }}>
              <ConversationList
                conversations={conversations}
                theme={theme}
                connected={connected}
                loading={loading}
                currentUserId={currentUserId}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                selectedChat={selectedChat}
                setSelectedChat={setSelectedChat}
                unreadCounts={unreadCounts}
              />
            </Box>
            <Box sx={{ flex: 1, height: '100%', minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
              <ChatView
                selectedChat={selectedChat}
                isMobile={isMobile}
                setSelectedChat={setSelectedChat}
                theme={theme}
                typingUsers={typingUsers}
                isSearchOpen={isSearchOpen}
                searchInChat={searchInChat}
                toggleSearch={toggleSearch}
                setSearchInChat={setSearchInChat}
                loadingMessages={loadingMessages}
                filteredMessages={filteredMessages}
                onRetryMessage={handleRetryMessage}
                loadingOlderMessages={loadingOlderMessages}
                hasMoreMessages={hasMoreMessages}
                showScrollButton={showScrollButton}
                handleScroll={handleScroll}
                scrollToBottom={scrollToBottom}
                messagesContainerRef={messagesContainerRef}
                messagesEndRef={messagesEndRef}
                connected={connected}
                onRefreshConversation={handleRefreshConversation}
                onMarkConversationRead={handleMarkConversationRead}
                menuActionFeedback={menuActionFeedback}
                setMenuActionFeedback={setMenuActionFeedback}
                onSendMessage={handleSendMessage}
                onSendFile={handleSendFile}
                onTyping={handleTyping}
                onStoppedTyping={handleStoppedTyping}
              />
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}
